import { FormEvent, useEffect, useState } from 'react';
import {
  PlanningCycle,
  PlanningObjective,
  PlanningObjectiveUpsertPayload,
} from '../../services/strategicPlanningApi';
import { PLANNING_OBJECTIVE_KINDS, PlanningObjectiveKindId } from '../../types/PlanningObjectiveKind';
import { ModalPortalShell } from '../ui/ModalPortalShell';
import { PlanningCancellationReasonField } from './PlanningCancellationReasonField';

type PlanningObjectiveFormModalProps = {
  open: boolean;
  cycles: PlanningCycle[];
  initial?: PlanningObjective | null;
  defaultCycleId?: string;
  defaultKind?: string;
  defaultSortOrder?: number;
  onClose: () => void;
  onSubmit: (cycleId: string, payload: PlanningObjectiveUpsertPayload) => Promise<void>;
};

function resolveDefaultKind(kind?: string): PlanningObjectiveKindId {
  return PLANNING_OBJECTIVE_KINDS.some((item) => item.id === kind)
    ? (kind as PlanningObjectiveKindId)
    : 'Estrategico';
}

const OBJECTIVE_STATUSES = [
  { value: 'Draft', label: 'Rascunho' },
  { value: 'Active', label: 'Ativo' },
  { value: 'Achieved', label: 'Atingido' },
  { value: 'NotAchieved', label: 'Não atingido' },
  { value: 'Cancelled', label: 'Cancelado' },
];

export function PlanningObjectiveFormModal({
  open,
  cycles,
  initial,
  defaultCycleId,
  defaultKind,
  defaultSortOrder,
  onClose,
  onSubmit,
}: PlanningObjectiveFormModalProps) {
  const [cycleId, setCycleId] = useState('');
  const [kind, setKind] = useState('Estrategico');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState('Draft');
  const [cancellationReason, setCancellationReason] = useState('');
  const [plannedStartDate, setPlannedStartDate] = useState('');
  const [plannedEndDate, setPlannedEndDate] = useState('');
  const [saving, setSaving] = useState(false);

  const selectedCycle = cycles.find((cycle) => cycle.id === cycleId) ?? null;

  useEffect(() => {
    if (!open) return;

    setCycleId(initial?.cycleId ?? defaultCycleId ?? cycles[0]?.id ?? '');
    setKind(initial?.kind ?? resolveDefaultKind(defaultKind));
    setTitle(initial?.title ?? '');
    setDescription(initial?.description ?? '');
    setStatus(initial?.status ?? 'Draft');
    setCancellationReason(initial?.cancellationReason ?? '');
    setPlannedStartDate(initial?.plannedStartDate ?? '');
    setPlannedEndDate(initial?.plannedEndDate ?? '');
  }, [open, initial, defaultCycleId, defaultKind, cycles]);

  useEffect(() => {
    if (!open || initial) return;

    const cycle = cycles.find((item) => item.id === cycleId);
    if (!cycle) return;

    setPlannedStartDate(cycle.startDate);
    setPlannedEndDate(cycle.endDate);
  }, [open, initial, cycleId, cycles]);

  if (!open) return null;

  const selectedKind = PLANNING_OBJECTIVE_KINDS.find((item) => item.id === kind);
  const isEdit = Boolean(initial);

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!cycleId) return;
    if (status === 'Cancelled' && !cancellationReason.trim()) return;

    setSaving(true);
    try {
      await onSubmit(cycleId, {
        kind,
        title: title.trim(),
        description: description.trim() || undefined,
        status,
        cancellationReason: status === 'Cancelled' ? cancellationReason.trim() : undefined,
        sortOrder: initial?.sortOrder ?? defaultSortOrder ?? 0,
        plannedStartDate: plannedStartDate || undefined,
        plannedEndDate: plannedEndDate || undefined,
        indicators: kind === 'Estrategico'
          ? initial?.indicators.map((indicator) => ({
              name: indicator.name,
              unit: indicator.unit ?? undefined,
              valueKind: indicator.valueKind,
              targetValue: indicator.targetValue ?? undefined,
              currentValue: indicator.currentValue ?? undefined,
              qualitativeTarget: indicator.qualitativeTarget ?? undefined,
              qualitativeCurrent: indicator.qualitativeCurrent ?? undefined,
            })) ?? []
          : [],
      });
      onClose();
    } catch {
      // Erro já notificado pelo caller.
    } finally {
      setSaving(false);
    }
  }

  return (
    <ModalPortalShell open={open}>
        <form
          className="flex min-h-0 flex-1 flex-col"
          onSubmit={(event) => void handleSubmit(event)}
        >
          <div className="shrink-0 border-b border-secondary-200 px-5 py-4">
            <h2 className="erp-card-title">{isEdit ? 'Editar objetivo' : 'Novo objetivo'}</h2>
            <p className="erp-card-subtitle">
              Defina o tipo do objetivo. Indicadores permanecem exclusivos dos objetivos estratégicos.
            </p>
          </div>

          <div className="erp-card-body min-h-0 flex-1 space-y-4 overflow-y-auto">
          <label className="block">
            <span className="erp-label">Ciclo</span>
            <select
              className="erp-input"
              value={cycleId}
              onChange={(event) => setCycleId(event.target.value)}
              required
              disabled={isEdit}
            >
              <option value="">Selecione o ciclo</option>
              {cycles.map((cycle) => (
                <option key={cycle.id} value={cycle.id}>
                  {cycle.name}
                </option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="erp-label">Tipo de objetivo</span>
            <select
              className="erp-input"
              value={kind}
              onChange={(event) => setKind(event.target.value)}
            >
              {PLANNING_OBJECTIVE_KINDS.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </select>
            {selectedKind ? (
              <p className="mt-1 text-xs text-secondary-500">{selectedKind.description}</p>
            ) : null}
          </label>

          <label className="block">
            <span className="erp-label">Título</span>
            <input
              className="erp-input"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              required
              maxLength={300}
            />
          </label>

          <label className="block">
            <span className="erp-label">Descrição</span>
            <textarea
              className="erp-input min-h-[80px]"
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              maxLength={4000}
            />
          </label>

          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block">
              <span className="erp-label">Início planejado</span>
              <input
                type="date"
                className="erp-input"
                value={plannedStartDate}
                onChange={(event) => setPlannedStartDate(event.target.value)}
              />
            </label>

            <label className="block">
              <span className="erp-label">Fim planejado</span>
              <input
                type="date"
                className="erp-input"
                value={plannedEndDate}
                onChange={(event) => setPlannedEndDate(event.target.value)}
              />
            </label>
          </div>

          {selectedCycle && !initial ? (
            <p className="text-xs text-secondary-500">
              Período sugerido do ciclo {selectedCycle.name}: {selectedCycle.startDate} a {selectedCycle.endDate}.
            </p>
          ) : null}

          <label className="block">
            <span className="erp-label">Status</span>
            <select
              className="erp-input"
              value={status}
              onChange={(event) => setStatus(event.target.value)}
            >
              {OBJECTIVE_STATUSES.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          {status === 'Cancelled' ? (
            <PlanningCancellationReasonField
              value={cancellationReason}
              onChange={setCancellationReason}
            />
          ) : null}
          </div>

          <div className="erp-btn-actions shrink-0 border-t border-secondary-200 bg-white px-5 py-4">
            <button type="button" className="erp-btn-secondary" onClick={onClose} disabled={saving}>
              Cancelar
            </button>
            <button type="submit" className="erp-btn-primary" disabled={saving || !cycleId}>
              {saving ? 'Salvando…' : isEdit ? 'Salvar' : 'Criar objetivo'}
            </button>
          </div>
        </form>
    </ModalPortalShell>
  );
}
